import { Link } from '@inertiajs/react';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import type { HackerSubmissionsShow } from '@/types';
import { SubmissionStatusPill } from './components/SubmissionStatusPill';

function errorLines(error: string | null): string[] {
  if (!error) return [];
  return error
    .split('\n')
    .map((line) => line.replace(/^❌\s*/, '').trim())
    .filter(Boolean);
}

// /hacker/submissions/:sha — everything recorded about one pushed commit: its message,
// the verdict, why it failed, and where to see it on GitHub.
export default function Show({ project, submission }: HackerSubmissionsShow) {
  const errors = errorLines(submission.error);

  return (
    <div className="container mx-auto max-w-4xl p-6">
      <div className="space-y-6">
        <Link
          href="/hacker/submissions"
          className="inline-flex items-center gap-1 font-mono text-primary/70 text-sm hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Todas las submissions
        </Link>

        <div className="space-y-6 border-2 border-primary bg-background/80 p-6 sm:p-8">
          <div className="flex flex-col gap-3 border-primary/20 border-b pb-4 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="font-bold font-mono text-2xl text-primary">
              {submission.shortSha}
            </h1>
            <SubmissionStatusPill status={submission.status} className="text-sm" />
          </div>

          <dl className="space-y-4">
            <div className="border-primary border-l-4 pl-4">
              <dt className="font-bold text-primary/70 text-sm uppercase">
                Mensaje del commit
              </dt>
              <dd className="whitespace-pre-wrap font-mono text-primary">
                {submission.commitMessage || 'Sin mensaje'}
              </dd>
            </div>
            {(submission.commitAuthorName || submission.commitAuthorLogin) && (
              <div className="border-primary border-l-4 pl-4">
                <dt className="font-bold text-primary/70 text-sm uppercase">
                  Autor
                </dt>
                <dd className="text-primary">
                  {submission.commitAuthorName}
                  {submission.commitAuthorLogin && (
                    <span className="font-mono text-primary/70">
                      {' '}
                      @{submission.commitAuthorLogin}
                    </span>
                  )}
                </dd>
              </div>
            )}
            {submission.committedAt && (
              <div className="border-primary border-l-4 pl-4">
                <dt className="font-bold text-primary/70 text-sm uppercase">
                  Commiteado
                </dt>
                <dd className="text-primary">
                  {new Date(submission.committedAt).toLocaleString('es-ES')}
                </dd>
              </div>
            )}
            <div className="border-primary border-l-4 pl-4">
              <dt className="font-bold text-primary/70 text-sm uppercase">
                Validado
              </dt>
              <dd className="text-primary">
                {new Date(submission.createdAt).toLocaleString('es-ES')}
              </dd>
            </div>
            <div className="border-primary border-l-4 pl-4">
              <dt className="font-bold text-primary/70 text-sm uppercase">
                Commit completo
              </dt>
              <dd className="break-all font-mono text-primary text-sm">
                {submission.commitSha}
              </dd>
            </div>
            <div className="border-primary border-l-4 pl-4">
              <dt className="font-bold text-primary/70 text-sm uppercase">
                Repositorio
              </dt>
              <dd className="flex flex-col gap-1">
                {project.repoUrl ? (
                  <a
                    href={project.repoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary underline"
                  >
                    {project.repoUrl}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="text-primary/70">Sin repositorio</span>
                )}
                {submission.commitUrl && (
                  <a
                    href={submission.commitUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary underline"
                  >
                    Ver este commit en GitHub
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </dd>
            </div>
          </dl>

          {errors.length > 0 && (
            <div className="space-y-3">
              <h2 className="font-bold text-primary uppercase">
                Requisitos faltantes
              </h2>
              {errors.map((line) => (
                <div
                  key={line}
                  className="border-destructive border-l-4 pl-4 font-mono text-primary text-sm"
                >
                  {line}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
